import { ArrHlp } from '../../helpers/array.helper';
import { Nullish } from '../../types/typeish.type';
import { EstateEmailBrandingSettings } from '../email/email-branding.data';

export interface EstateSettingsData {
  id: string;
  integrations?: {
    sap?: string;
    weles?: WelesSettings;
    mieszczanin?: string;
    sauter?: boolean;
    webcon?: WebconSettings;
    bluebolt?: {
      noParkingCheck?: boolean;
    };
  };
  smsDefaults: Partial<SmsDefaultSettings> | null;
  newsDefaults: Partial<NewsDefaultSettings> | null;
  emailBranding?: EstateEmailBrandingSettings;

  notify?: EstateNotify;
}

export interface WelesSettings {
  code: string;
  removeEmail: boolean;
  getDocuments: boolean;
}

export interface WebconSettings {
  code: string;
  testEnv: boolean;
  estateStruct: boolean;
  reckonings: boolean;
  /** Imports contract documents, property plans, and investment documents. */
  documents?: boolean;
  defectTypes: boolean;
  handoverDefects: boolean;
  warrantyDefects: boolean;
  meters: boolean;
  appAutoInvite: boolean;
}

export interface FooterDefaultSettings {
  footerContent: string;
  useFooter: boolean;
}

// ? structures for future reference (if someone wanted to make more settings for sms / news)
export type NewsDefaultSettings = FooterDefaultSettings;
export type SmsDefaultSettings = FooterDefaultSettings;

export const ESTATE_NOTIFY_DAYS = [90, 60, 30, 14, 7, 3, 1] as const;
/** Used for const / default values, for db data we should use just "number" */
export type EstateNotifyDaysType = (typeof ESTATE_NOTIFY_DAYS)[number];

export const ESTATE_NOTIFY_KEYS = ['agreement', 'subcontractor', 'review', 'renovation', 'utility', 'insurance'] as const;
export type EstateNotifyKey = (typeof ESTATE_NOTIFY_KEYS)[number];

// !!! Adding new X notify key remember about adding:
// [ ] X value in EmailTemplateType
// [ ] Email template, and it's id in SendMailHelper.getEmailTemplateId(X),
// [ ] Translations: estate.{X}End, estate.{X}EndDays
// [ ] Backend handler, ald all ".indexOn" rules used by it
// [ ] Rule "employee".".indexOn"."permissions/mailing/{X}"
// [ ] Add field in company and superadmin settings"

/** Estate agreement/contract end notifications */
export type EstateNotify = Required<Record<EstateNotifyKey, number[]>>;

export namespace EstateNotify {
  /**
   * Creates empty estate notification days map.
   * @return {EstateNotify}
   */
  export function empty(): EstateNotify {
    return Object.fromEntries(ESTATE_NOTIFY_KEYS.map((x) => [x, []])) as any;
  }

  /**
   * Compares two estate notification maps.
   * @param {EstateNotify | Nullish} x first map
   * @param {EstateNotify | Nullish} y second map
   * @return {boolean}
   */
  export function areEqual(x: EstateNotify | Nullish, y: EstateNotify | Nullish): boolean {
    if (!x) x = EstateNotify.empty();
    if (!y) y = EstateNotify.empty();
    for (const type of ESTATE_NOTIFY_KEYS) {
      if (!ArrHlp.areEqual(x[type] ?? [], y[type] ?? [])) return false;
    }
    return true;
  }
}
